import {
  createListCollection,
  Grid,
  HStack,
  Portal,
  Select,
  Text,
  useSlotRecipe,
  VStack,
  Icon as IconWrapper,
  Card,
} from '@chakra-ui/react';
import { useState } from 'react';
import DocumentCard from '../../components/DocumentCard';
import { useAppSelector } from '../../lib/hooks';
import { DocumentResultType, SortOrderType } from '../../lib/state/ViewTypes';
import sortNodes from '../../lib/Sort';
import Pagination from './Pagination';
import { v4 as uuidv4 } from 'uuid';
import Topbar from './Topbar/Topbar';
import { selectRecipe } from '@/theme';
import { MdOutlineSort } from 'react-icons/md';

export default function ResearchPage() {
  const documentSearchType = useAppSelector(
    (state) => state.viewReducer.documents.search.type,
  ) as keyof DocumentResultType;
  const documentResultStructure = useAppSelector((state) => state.viewReducer.documents.result);

  const isDocumentResultType = documentSearchType === 'CONTEXT' || documentSearchType === 'EXACT';
  const selectedNodes =
    isDocumentResultType && documentResultStructure[documentSearchType]?.data
      ? documentResultStructure[documentSearchType].data
      : [];

  const [sortOrder, setSortOrder] = useState<SortOrderType>('default');

  return (
    <VStack
      style={{
        width: '100%',
        height: '100%',
      }}
      background="background"
      padding={4}
    >
      <Topbar />

      <VStack
        style={{
          borderRadius: '0.375rem',
          width: '100%',
          height: '100%',
        }}
        background="midCardBG"
        overflowY="auto"
        overflowX="hidden"
        padding={4}
        gap={4}
      >
        <SortDocumentCards
          totalCards={selectedNodes.length}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />
        <Grid
          style={{
            width: '100%',
            height: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'thin',
            justifyItems: 'center',
            borderRadius: '0.375rem',
          }}
          templateColumns={'repeat(auto-fit, minmax(380px, 1fr))'}
          padding={4}
          gap={4}
        >
          {sortNodes(selectedNodes, sortOrder).map((node) => (
            <DocumentCard
              key={
                selectedNodes.filter((n) => n.id === node.id).length > 1
                  ? node.id + uuidv4()
                  : node.id
              }
              id={node.id}
            />
          ))}
        </Grid>
        <Pagination style={{ width: '100%' }} />
      </VStack>
    </VStack>
  );
}

function SortDocumentCards(props: {
  sortOrder: string;
  setSortOrder: React.Dispatch<React.SetStateAction<SortOrderType>>;
  totalCards: number;
}) {
  const sortOptions = createListCollection({
    items: [
      { label: 'Date (newest first)', value: 'date' },
      { label: 'Relevance', value: 'relevance' },
      { label: 'Best Match', value: 'default' },
    ],
  });

  const recipe = useSlotRecipe({ recipe: selectRecipe });
  const style = recipe();

  return (
    <HStack
      style={{
        width: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Select.Root
        css={style.root}
        style={{
          width: '200px',
        }}
        size="sm"
        borderRadius="0.375rem"
        background="cardDefault"
        _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
        _focus={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          props.setSortOrder(e.target.value as SortOrderType)
        }
        defaultValue={[props.sortOrder]}
        collection={sortOptions}
      >
        <Select.HiddenSelect />

        <Select.Control outline="none" css={style.control} width="100%">
          <Select.Trigger outline="none" css={style.trigger} width="100%">
            <HStack width="100%" gap={2}>
              <IconWrapper as={MdOutlineSort} color="secondaryText" />
              <Select.ValueText
                width="100%"
                placeholder="Sort by"
                color="secondaryText"
                fontWeight={500}
              />
            </HStack>
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator color="secondaryText" />
          </Select.IndicatorGroup>
        </Select.Control>

        <Portal>
          <Select.Positioner>
            <Select.Content background="cardDefault">
              {sortOptions.items.map((option: { label: string; value: SortOrderType }) => (
                <Select.Item
                  key={option.value}
                  item={option}
                  css={{
                    '&[data-state="checked"]': {
                      background: 'cardActive',
                    },
                  }}
                  color="secondaryText"
                  _focus={{ bg: 'cardDefault' }}
                  _hover={{ bg: 'cardHover', transition: 'background-color 0.2s' }}
                  _active={{ bg: 'cardActive', transition: 'background-color 0.2s' }}
                  fontWeight={props.sortOrder === option.value ? 500 : 'none'}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      <Card.Root
        border="none"
        outline="none"
        background="cardDefault"
        borderRadius="0.375rem"
        style={{
          height: '100%',
        }}
      >
        <Card.Body height="100%" justifyContent="center" p={1} px={3}>
          <Text color="secondaryText" fontWeight={500}>
            Page results: {props.totalCards}
          </Text>
        </Card.Body>
      </Card.Root>
    </HStack>
  );
}
